<template>
  <div
    class="base-type-new"
    :class="baseTypeThemeClass"
    :style="baseTypeThemeStyle"
  >
    <section class="base-type-new__hero">
      <div class="base-type-new__icon">
        <Icon :icon="baseTypeTheme.icon" />
      </div>
      <div>
        <span class="base-type-new__eyebrow">{{ baseTypeTheme.eyebrow }}</span>
        <h1>{{ title }}</h1>
        <p v-if="subtitle" class="mb-0">{{ subtitle }}</p>
      </div>
    </section>

    <slot name="alert" />

    <Form
      @submit="createObject"
      v-slot="{ errors }"
      :validation-schema="schema"
      ref="form"
      class="base-type-new__form"
    >
      <fieldset class="fieldset">
        <legend class="base-type-new__legend">{{ legend }}</legend>

        <div class="form-floating">
          <Field
            type="text"
            name="name"
            :id="inputId"
            class="form-control"
            :class="{'is-invalid': errors.name}"
            :placeholder="inputLabel"
            autocomplete="off"
            required
          />
          <label :for="inputId">{{ inputLabel }}</label>
          <ErrorMessage name="name" class="mt-1 d-block invalid-feedback" />
        </div>

        <p v-if="hint" class="base-type-new__hint mt-3 mb-0">{{ hint }}</p>
      </fieldset>

      <fieldset
        v-if="requireCategory"
        class="fieldset"
        :class="{'fieldset--category-rows': categoryLayout === 'rows'}"
      >
        <legend class="base-type-new__legend">{{ categoryLegend }}</legend>

        <p v-if="categoriesLoading" class="base-type-new__hint mb-0">
          Kategorien werden geladen...
        </p>

        <div v-else-if="categories.length" class="base-type-new__category-groups">
          <div
            v-for="(groupCategories, groupName) in groupedCategories"
            :key="groupName"
            class="base-type-new__category-group"
          >
            <h3 class="base-type-new__category-group-title">{{ groupName }}</h3>
            <div class="category-list">
              <div
                v-for="category in groupCategories"
                :key="category.id"
                class="category-item"
              >
                <Field
                  type="radio"
                  name="category_id"
                  class="form-check-input"
                  :id="`cb-new-category-${category.id}`"
                  :value="category.id"
                  :class="{'is-invalid': errors.category_id}"
                />
                <label :for="`cb-new-category-${category.id}`">
                  {{ categoryName(category) }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <p v-else class="base-type-new__hint mb-0">
          Keine Kategorien verfugbar.
        </p>

        <ErrorMessage name="category_id" class="mt-1 d-block invalid-feedback" />
      </fieldset>

      <div class="base-type-new__actions">
        <router-link :to="{ name: cancelRouteName }" class="btn btn-link base-type-new__cancel">
          {{ cancelLabel }}
        </router-link>
        <button type="submit" class="btn btn-warning" :disabled="submitDisabled">{{ submitLabel }}</button>
      </div>
    </Form>
  </div>
</template>

<script>
import {ErrorMessage, Field, Form} from "vee-validate";
import Yup from '@/yup'
import {BASE_TYPES} from "@/stores/constants";
import {useCategoryStore} from "@/stores/category";
import {useLocalizedObjectStore} from "@/stores/localized_objects";
import {getBaseTypeTheme, getBaseTypeThemeClass, getBaseTypeThemeStyle} from "@/utils/baseTypeTheme";
import {normalizeApiValidationErrors} from "@/utils/apiValidationErrors";
import {MAX_OBJECT_NAME_LENGTH} from "@/utils/objectFormPolicy";

export default {
  name: "BaseTypeNewForm",
  components: {
    ErrorMessage,
    Field,
    Form,
  },
  props: {
    baseType: {
      type: String,
      required: true,
      validator: (value) => ['accommodations', 'offers', 'events', 'discover'].includes(value),
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    legend: {
      type: String,
      default: 'Eintrag benennen',
    },
    inputLabel: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: null,
    },
    submitLabel: {
      type: String,
      required: true,
    },
    cancelLabel: {
      type: String,
      default: 'Abbrechen',
    },
    cancelRouteName: {
      type: String,
      required: true,
    },
    editRouteName: {
      type: String,
      required: true,
    },
    requireCategory: {
      type: Boolean,
      default: false,
    },
    categoryLegend: {
      type: String,
      default: 'Kategorie auswählen',
    },
    categoryLayout: {
      type: String,
      default: 'columns',
      validator: (value) => ['columns', 'rows'].includes(value),
    },
  },
  setup() {
    return {
      categoryStore: useCategoryStore(),
      localizedObjectStore: useLocalizedObjectStore(),
    }
  },
  data() {
    return {
      categories: [],
      categoriesLoading: false,
    }
  },
  created() {
    if (this.requireCategory) {
      this.fetchCategories()
    }
  },
  computed: {
    schema() {
      const shape = {
        name: Yup.string().max(MAX_OBJECT_NAME_LENGTH).required().label(this.inputLabel),
      }

      if (this.requireCategory) {
        shape.category_id = Yup.number()
          .transform((value, originalValue) => originalValue === '' ? null : value)
          .required('Bitte eine Kategorie auswählen')
          .label('Kategorie')
      }

      return Yup.object().shape(shape)
    },
    baseTypeTheme() {
      return getBaseTypeTheme(this.baseType)
    },
    baseTypeThemeClass() {
      return getBaseTypeThemeClass(this.baseType)
    },
    baseTypeThemeStyle() {
      return getBaseTypeThemeStyle(this.baseType)
    },
    inputId() {
      return `form-${this.baseType}-new-name`
    },
    groupedCategories() {
      return this.categories.reduce((groups, category) => {
        const groupName = category.group_name || 'Weitere Kategorien'
        groups[groupName] = groups[groupName] || []
        groups[groupName].push(category)
        return groups
      }, {})
    },
    submitDisabled() {
      return this.requireCategory && (this.categoriesLoading || !this.categories.length)
    },
  },
  methods: {
    fetchCategories() {
      this.categoriesLoading = true
      this.categoryStore.index({base_type: BASE_TYPES[this.baseType] ?? this.baseType})
        .then(({data}) => {
          this.categories = Array.isArray(data) ? data : data?.categories || []
        })
        .finally(() => {
          this.categoriesLoading = false
        })
    },
    categoryName(category) {
      return category?.name || category?.label || category?.title || category?.shortname || `#${category?.id}`
    },
    createObject(object) {
      object.base_type = BASE_TYPES[this.baseType] ?? ''
      if (this.requireCategory) {
        object.categories_data = [{
          id: Number(object.category_id),
          checked: true,
        }]
        delete object.category_id
      }
      this.localizedObjectStore.apiCreate(object).then(({data}) => {
        this.$router.push({
          name: this.editRouteName,
          params: {
            id: data.object.id,
            lang: data.object.lang,
          },
        })
      }).catch((error) => {
        if (error.response) {
          this.$refs.form.setErrors(normalizeApiValidationErrors(error.response.data))
        } else {
          throw error
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.base-type-new {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.base-type-new__hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 1.35rem;
  border: 1px solid var(--base-type-border);
  background: #ffffff;
  border-left: 0.45rem solid var(--base-type-primary);
}

.base-type-new__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.35rem;
  height: 3.35rem;
  background: var(--base-type-soft);
  color: var(--base-type-primary);
  font-size: 1.45rem;
}

.base-type-new__eyebrow {
  color: var(--base-type-primary);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.base-type-new h1 {
  margin: 0.1rem 0 0.25rem;
  color: var(--base-type-text);
}

.base-type-new__hero p {
  color: var(--base-type-text);
}

.base-type-new__form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
}

.base-type-new .fieldset {
  width: 100%;
  margin: 0;
  border: 1px solid var(--base-type-border);
  border-top: 0.22rem solid var(--base-type-primary);
  background: color-mix(in srgb, var(--base-type-soft) 42%, #ffffff);
  box-shadow: 0 10px 24px rgba(10, 33, 48, 0.05);
  transition: box-shadow 0.2s ease;
}

.base-type-new .fieldset:hover {
  box-shadow: 0 14px 30px rgba(10, 33, 48, 0.07);
}

.base-type-new__legend {
  position: relative;
  float: none;
  width: auto;
  margin: -0.22rem 0 1.2rem;
  padding: 0.45rem calc(0.9rem + 0.22rem) 0.35rem;
  border: none;
  border-top: 0.22rem solid var(--base-type-primary);
  background: color-mix(in srgb, var(--base-type-soft) 42%, #ffffff);
  color: var(--base-type-text);
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.base-type-new__legend::before,
.base-type-new__legend::after {
  content: '';
  position: absolute;
  top: 0;
  width: 0.22rem;
  height: 50%;
  background: var(--base-type-primary);
}

.base-type-new__legend::before {
  left: 0;
}

.base-type-new__legend::after {
  right: 0;
}

.base-type-new__hint {
  color: var(--base-type-text);
  font-size: 0.9rem;
  opacity: 0.8;
}

.base-type-new__category-groups {
  column-count: 4;
  column-gap: 1.4rem;
  width: 100%;
}

.base-type-new__category-group {
  display: inline-block;
  width: 100%;
  min-width: 0;
  margin: 0 0 1.1rem;
  break-inside: avoid;
  page-break-inside: avoid;
}

.base-type-new__category-group-title {
  margin: 0 0 0.55rem;
  color: var(--base-type-text);
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.25;
}

.base-type-new .category-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.4rem;
}

.base-type-new .category-item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  width: 100%;
  min-height: 2.35rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid color-mix(in srgb, var(--base-type-border) 70%, #ffffff);
  background: rgba(255, 255, 255, 0.48);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.base-type-new .category-item:hover {
  border-color: var(--base-type-border);
  background: #ffffff;
}

.base-type-new .category-item .form-check-input {
  flex: 0 0 auto;
  margin-top: 0.18rem;
  border-color: var(--base-type-border);
}

.base-type-new .category-item .form-check-input:checked {
  border-color: var(--base-type-primary);
  background-color: var(--base-type-primary);
}

.base-type-new .category-item label {
  flex: 1 1 auto;
  margin: 0;
  color: var(--base-type-text);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
}

@media (max-width: 1399.98px) {
  .base-type-new__category-groups {
    column-count: 3;
  }
}

@media (max-width: 991.98px) {
  .base-type-new__category-groups {
    column-count: 2;
  }
}

@media (max-width: 575.98px) {
  .base-type-new__category-groups {
    column-count: 1;
  }
}

.base-type-new .fieldset--category-rows .base-type-new__category-groups {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.6rem;
  column-count: initial;
  column-gap: initial;
}

.base-type-new .fieldset--category-rows .base-type-new__category-group {
  display: block;
  margin: 0;
}

.base-type-new .fieldset--category-rows .category-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

@media (max-width: 991.98px) {
  .base-type-new .fieldset--category-rows .category-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .base-type-new .fieldset--category-rows .category-list {
    grid-template-columns: 1fr;
  }
}

.base-type-new__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.base-type-new__cancel {
  color: var(--base-type-text);
  text-decoration: none;
}

.base-type-new__cancel:hover,
.base-type-new__cancel:focus-visible {
  color: var(--base-type-primary);
  text-decoration: underline;
}
</style>
